import {
  Button,
  Label,
  LoadingState,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  toast,
} from '@sitewise/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FileUp } from 'lucide-react';
import { useRef, useState } from 'react';

import {
  confirmDeviceDocument,
  fetchDevice,
  presignDeviceDocument,
  type DeviceWithDeviceType,
} from '../../../api/compliance.js';
import { ApiError } from '../../../lib/api-client.js';

interface Props {
  device: DeviceWithDeviceType;
  onClose: () => void;
}

const MAX_BYTES = 50 * 1024 * 1024;

export function DeviceDocumentsSheet({ device, onClose }: Props) {
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploadingName, setUploadingName] = useState<string | null>(null);

  const detailQuery = useQuery({
    queryKey: ['compliance', 'device', device.id],
    queryFn: () => fetchDevice(device.id),
  });

  async function handleUpload(file: File) {
    if (file.size > MAX_BYTES) {
      toast.error('File is larger than 50 MB.');
      return;
    }
    setUploadingName(file.name);
    try {
      const presigned = await presignDeviceDocument(device.id, {
        kind: 'device_document',
        filename: file.name,
        contentType: file.type || 'application/octet-stream',
        sizeBytes: file.size,
      });
      const put = await fetch(presigned.uploadUrl, {
        method: 'PUT',
        headers: presigned.headers,
        body: file,
      });
      if (!put.ok) throw new Error(`Upload failed (${put.status})`);
      await confirmDeviceDocument(device.id, presigned.attachment.id);
      toast.success('Document uploaded');
      void queryClient.invalidateQueries({ queryKey: ['compliance', 'device', device.id] });
      void queryClient.invalidateQueries({ queryKey: ['compliance', 'devices'] });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploadingName(null);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="flex flex-col overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Device documents</SheetTitle>
          <SheetDescription>
            {device.label} · {device.deviceType.name}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={fileRef}
              type="file"
              className="hidden"
              accept="image/*,.pdf,application/pdf"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void handleUpload(file);
              }}
            />
            <Button
              size="sm"
              onClick={() => fileRef.current?.click()}
              disabled={Boolean(uploadingName)}
            >
              <FileUp className="h-3.5 w-3.5" />
              {uploadingName ? `Uploading ${uploadingName}…` : 'Upload certificate / manual'}
            </Button>
            <Label className="text-xs text-fg-muted">PDF or image, max 50 MB</Label>
          </div>

          {detailQuery.isLoading ? (
            <LoadingState rows={3} />
          ) : detailQuery.isError ? (
            <p className="text-sm text-danger">Could not load documents.</p>
          ) : (detailQuery.data?.documents.length ?? 0) === 0 ? (
            <p className="text-sm text-fg-muted">No documents on this device yet.</p>
          ) : (
            <ul className="divide-y divide-border rounded border border-border">
              {detailQuery.data!.documents.map((doc) => (
                <li
                  key={doc.id}
                  className="flex items-center justify-between gap-2 px-3 py-2 text-sm"
                >
                  <div className="min-w-0">
                    <div className="truncate font-medium">{doc.originalFilename}</div>
                    <div className="text-xs text-fg-muted">
                      {doc.kind} · {new Date(doc.uploadedAt).toLocaleString()}
                    </div>
                  </div>
                  {doc.signedUrl ? (
                    <a
                      href={doc.signedUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm text-primary hover:underline"
                    >
                      View
                    </a>
                  ) : (
                    <span className="text-xs text-fg-muted">No preview</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
